import { Link } from 'react-router-dom';
import type { Section } from '../sections/sections';
import { FeatureCard } from './FeatureCard';
import './SectionContent.css';

interface SectionContentProps {
  section: Section;
  isActive: boolean;
}

/**
 * Renders a parallax section with the headline pattern:
 * Big white text with one italic green accent word.
 */
export function SectionContent({ section, isActive }: SectionContentProps) {
  const isHero = section.id === 'hero';
  const footerCta = section.footerCta;

  // Parse headline: replace <accent>word</accent> with styled span,
  // or append the accent word at the end
  const renderHeadline = () => {
    if (section.headline.includes('<accent>')) {
      const parts = section.headline.split(/<accent>|<\/accent>/);
      return (
        <>
          {parts[0]}
          <em className="section__accent">{parts[1]}</em>
          {parts[2]}
        </>
      );
    }
    return (
      <>
        {section.headline} <em className="section__accent">{section.accentWord}</em>
        {section.subtitle && ` ${section.subtitle}`}
      </>
    );
  };

  return (
    <section
      id={section.id}
      className={`section ${isActive ? 'section--active' : ''} ${isHero ? 'section--hero' : ''}`}
    >
      <div className={`section__inner ${footerCta ? 'section__inner--with-footer-cta' : ''}`}>
        <div
          className={`section__layout ${section.featureCardPosition ? `section__layout--${section.featureCardPosition}` : 'section__layout--center'}`}
        >
          <div className={`section__text ${isActive ? 'section__text--visible' : ''}`}>
            <h2 className={`section__headline ${isHero ? 'section__headline--hero' : ''}`}>
              {renderHeadline()}
            </h2>

            {isHero && section.secondSubtitle && (
              <p className="section__subtitle">{section.secondSubtitle}</p>
            )}

            {!isHero && section.sub && <p className="section__subtitle">{section.sub}</p>}

            {section.ctas && (
              <div className="section__ctas">
                {section.ctas.map((cta) =>
                  cta.href ? (
                    <a
                      key={cta.label}
                      href={cta.href}
                      className={`section__cta section__cta--${cta.variant}`}
                    >
                      {cta.label}
                      <span className="section__cta-icon">{cta.icon}</span>
                    </a>
                  ) : (
                    <button key={cta.label} className={`section__cta section__cta--${cta.variant}`}>
                      {cta.label}
                      <span className="section__cta-icon">{cta.icon}</span>
                    </button>
                  ),
                )}
              </div>
            )}
          </div>

          {section.featureCard && <FeatureCard card={section.featureCard} isVisible={isActive} />}
        </div>

        {footerCta && (
          <div className="section__footer-cta">
            <Link to={footerCta.to} className="section__cta section__cta--accent">
              {footerCta.label}
              <span className="section__cta-icon">›</span>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
